import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Cookies } from 'react-cookie';
import withCSRF from '../../../../commonComponents/withCSRF';
import translate from '../../../../commonComponents/translate';
import { compose } from '../../../../services/utilityFunctions';
import Form from '../../../../commonComponents/forms/Form';
import FormInput from '../../../../commonComponents/forms/FormInput';
import Request from '../../../../services/request';
import Notifications from '../../../../services/notifications';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import MarketInfosContext from '../../../../contextProviders/MarketInfosContext';
import TermsAndPolicyCheckbox from '../../../../commonComponents/forms/TermsAndPolicyCheckbox';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import FormUserTypeInput from '../../../../commonComponents/forms/FormUserTypeInput';

function SignUpForm({ t, _csrf, onSignUpComplete, languages, cookies }) {
  const formId = 'signUpForm';

  const [postingForm, setPostingForm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [confirm_pass, setConfirmPass] = useState('');
  const [language, setLanguage] = useState('en');
  const [userType, setUserType] = useState('customer');

  const validationRules = {
    firstName: { required: true },
    lastName: { required: true },
    email: { required: true, email: true },
    password: { required: true, minlength: 6 },
    language: { required: true },
    confirm_password: { required: true, equalTo: '#password' },
    type: { required: false },
  };
  const { marketInfos } = useContext(MarketInfosContext);
  const { tenant } = useContext(ConfigsContext);
  const usersCanChooseTheirType = tenant.settings?.usersCanChooseTheirType;

  const submitButtonClasses = cx(
    'btn',
    'btn-dynamic',
    'm-btn',
    'm-btn--pill',
    'm-btn--custom',
    'm-btn--air',
    'm-login__btn',
    { 'm-loader': postingForm },
    { 'm-loader--right': postingForm },
    { 'm-loader--light': postingForm },
  );

  const onValidated = () => {
    setPostingForm(true);
    let params = {
      email,
      firstName,
      lastName,
      username: `${firstName} ${lastName}`,
      password: pass,
      language,
    };
    if (marketInfos && marketInfos.localCountry) {
      params.shippingCountry = marketInfos.localCountry;
    }
    if (marketInfos && marketInfos.localCurrency) {
      params.currency = marketInfos.localCurrency;
    }
    if (marketInfos && marketInfos.localLanguage) {
      params.language = params.language || marketInfos.localLanguage;
    }
    if (usersCanChooseTheirType) {
      params.type = userType;
    }
    // If sponsoredBy exists saved
    if (cookies.get('sponsoredBy')) {
      params = {
        ...params,
        sponsoredBy: cookies.get('sponsoredBy'),
      };
    }
    new Request(_csrf)
      .post('/signin/signup', params)
      .done(onSignUpComplete)
      .fail(err => {
        setPostingForm(false);
        Notifications.showErrorFromRequest(err);
      });
  };

  // Checkbox handling
  const [checkbox, setCheckbox] = useState(false);
  const handleCheckbox = () => {
    setCheckbox(!checkbox);
  };

  return (
    <div className="m-login__signup">
      <div className="m-login__head">
        <h3 className="m-login__title">{t('login_noAccount')}</h3>
      </div>
      <Form
        className="m-login__form m-form"
        id={formId}
        submitMethod={onValidated}
        validationRules={validationRules}
      >
        {submitHandler => (
          <>
            <FormInput
              type="text"
              name="firstName"
              inputProps={{ autoComplete: 'off' }}
              placeholder={t('name_holder')}
              value={firstName}
              onChange={setFirstName}
              inputClassName="signup-input"
            />
            <FormInput
              type="text"
              name="lastName"
              inputProps={{ autoComplete: 'off' }}
              placeholder={t('lastName_holder')}
              value={lastName}
              onChange={setLastName}
              inputClassName="signup-input"
            />
            <FormInput
              type="email"
              name="email"
              inputProps={{ autoComplete: 'off' }}
              placeholder={t('email_holder')}
              value={email}
              onChange={setEmail}
              inputClassName="signup-input"
            />
            <FormInput
              type="password"
              placeholder={t('pass_holder')}
              id="password"
              name="password"
              value={pass}
              onChange={setPass}
              inputClassName="signup-input"
            />
            <FormInput
              type="password"
              placeholder={t('reTypePass_holder')}
              id="confirm_password"
              name="confirm_password"
              value={confirm_pass}
              onChange={setConfirmPass}
              inputClassName="signup-input"
            />
            {usersCanChooseTheirType && (
              <FormUserTypeInput
                value={userType}
                onChange={setUserType}
                showLabel={false}
                inputClassName="signup-input"
                containerClassName="col-12"
              />
            )}
            <div className="form-group m-form__group">
              <select
                className="form-control m-input signup-input"
                name="language"
                value={language}
                onChange={({ currentTarget }) =>
                  setLanguage(currentTarget.value)
                }
              >
                {languages.map(l => (
                  <option key={l.code} value={l.code}>
                    {l.name}
                  </option>
                ))}
              </select>
              <TermsAndPolicyCheckbox
                checkboxValue={checkbox}
                handleCheckbox={handleCheckbox}
                tenant={tenant}
              />
            </div>
            <div className="m-login__form-action">
              <button
                type="submit"
                className={submitButtonClasses}
                disabled={postingForm || !checkbox}
                onClick={submitHandler}
                data-testid="create_account"
              >
                {t('login_createAccount')}
              </button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
}

SignUpForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  onSignUpComplete: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
};

export default compose(translate, withCSRF)(SignUpForm);
